import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { FormProvider, useForm } from 'react-hook-form'
import { MutateFunction, useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { Alert, Container, Snackbar, styled } from '@mui/material'
import { AxiosError } from 'axios'

import { DefaultResponse } from 'common/interfaces'
import { useAuth } from 'context/SearchAuth'
import api from 'utils/api'
import { getCompanySlug, useFlowSlug } from 'utils/flow'

import LoginContainer from './Container'
import LoginHeader from './LoginHeader'
import LoginPage from './LoginPage'
import LoginToolbar from './Toolbar'
import VerifyPage from './VerifyPage'

const ViewContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}))

type LoginStage = 'email' | 'verifying'
type CreateOTPResponse = DefaultResponse
interface CreateOTPRequest {
  company: string
  email: string
  flow: string
}
interface VerifyRequest {
  email: string
  otp: string
  company: string
  flow: string
}
interface VerifyResponse {
  access: string
  refresh: string
}

interface ErrorResponse {
  error?: string // Optional property for error messages
  details?: string // Optional property for additional details
}

const sendEmail: MutateFunction<
  CreateOTPResponse,
  AxiosError<ErrorResponse>,
  CreateOTPRequest
> = async ({ company, email, flow }) => {
  const response = await api.post<CreateOTPResponse>('verifications/otp/', { company, email, flow })

  return response.data
}

const sendVerification: MutateFunction<
  VerifyResponse,
  AxiosError<ErrorResponse>,
  VerifyRequest
> = async ({ email, otp, company, flow }) => {
  const response = await api.post('verifications/otp/verify/', { email, otp, company, flow })

  return response.data
}

export default function SessionSearchLoginView(): JSX.Element {
  const navigate = useNavigate()
  const { login, error, setError } = useAuth()
  const [loginStage, setLoginStage] = useState<LoginStage>('email')
  const [openToast, setToastOpen] = useState(false)

  const sendOTP = useMutation<CreateOTPResponse, AxiosError, CreateOTPRequest>(sendEmail)
  const verify = useMutation<VerifyResponse, AxiosError, VerifyRequest>(sendVerification)

  const form = useForm({ mode: 'onBlur' })

  const flowSlug = useFlowSlug() ? useFlowSlug().split('/')[0] : ''

  const onNext = async (): Promise<void> => {
    const isValid = await form.trigger('email')

    if (isValid) {
      try {
        await sendOTP.mutateAsync({
          email: form.getValues().email,
          company: getCompanySlug(),
          flow: flowSlug,
        })

        setLoginStage('verifying')
      } catch (error) {
        if (error instanceof AxiosError && error.response?.data) {
          const errorMessage =
            error.response.data.error ?? error.response.data.details ?? 'An unknown error occurred'
          form.setError('email', { message: errorMessage })
        }
      }
    }
  }

  const onPrev = (): void => setLoginStage('email')

  const onSubmit = form.handleSubmit(async ({ email, otp }): Promise<void> => {
    try {
      if (loginStage === 'email') {
        await onNext()
        return
      }

      await verify.mutateAsync(
        { email, otp, company: getCompanySlug(), flow: flowSlug },
        {
          onSuccess: async (response) => {
            console.log(response)
            const { access, refresh } = response

            await login(access, refresh)

            navigate(`/${flowSlug}/session-search`, { replace: true })
          },
          onError: ({ message }) => {
            form.setError('otp', { message })
          },
        }
      )
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        if (error.response !== undefined) {
          form.setError('otp', { message: error.response.data.details })
        }
      }
    }
  })

  useEffect(() => {
    if (error) {
      setToastOpen(true)
    }
  }, [error])

  const handleCloseToast = (): void => {
    setToastOpen(false)
    setError('')
  }

  return (
    <ViewContainer disableGutters>
      <Helmet>
        <title>Log In</title>
      </Helmet>
      <LoginHeader onPrev={loginStage === 'verifying' ? onPrev : undefined} />
      <Snackbar
        open={openToast}
        autoHideDuration={6000} // Automatically close after 6 seconds
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Position the toast
      >
        <Alert onClose={handleCloseToast} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <LoginToolbar />
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <LoginContainer onSubmit={onSubmit}>
        <FormProvider {...form}>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          {loginStage === 'email' ? <LoginPage onNext={onNext} /> : <VerifyPage />}
        </FormProvider>
      </LoginContainer>
    </ViewContainer>
  )
}
